import React, { useState } from "react";
import { Badge, Button } from "antd";
import { useNavigate, useParams } from "react-router";
import UserProfileLayout, {
  AvatarCard,
  InfoSection,
  StatusBadge,
  type InfoItem,
} from "../../../Common/UserProfileLayout";
import dayjs from "dayjs";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import { getUserStatusColor } from "..";
import { useUserGetById } from "../../../../hooks/api/users";
import EditModal from "../Edit";
import { useAuth, UserRoleType } from "../../../../store/auth";
import { getUserRequestPath } from "../../../../utils";

interface LeaderData {
  id: string;
  fullName: string;
  subtitle: string;
  role: string;
  avatar?: string;
  phone: string;
  email: string;
  address: string;
  activeVaads: number;
  status: "active" | "inactive";
  joinDate: string;
}

const mockLeader: LeaderData = {
  id: "1",
  fullName: "David Cohen",
  subtitle: "Rabbi",
  role: "Leader",
  avatar: "https://example.com/avatar.jpg",
  phone: "(555) 123-4567",
  email: "olive.green@mail.com",
  address: "123 Main St, Brooklyn, NY 11201",
  activeVaads: 15,
  status: "active",
  joinDate: "15 Jan 2024",
};

const LeaderViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userGetById = useUserGetById(id);
  const [editOpen, setEditOpen] = useState(false);
  const leader = mockLeader;
  const { user } = useAuth();
  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  const contactItems: InfoItem[] = [
    { label: "Full Name", value: userGetById.data?.fullName || "-" },
    { label: "Phone", value: userGetById.data?.phone || "-" },
    { label: "Email", value: userGetById.data?.email || "-" },
    { label: "Address", value: userGetById.data?.address || "-" },
  ];

  const generalItems: InfoItem[] = [
    {
      label: "Role",
      value:
        (userGetById.data?.role ?? "")
          .replace(/_/g, " ")
          .replace(/^\w/, (c) => c.toUpperCase()) || "-",
    },
    {
      label: "Active Vaads",
      value: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{userGetById.data?.memberships?.length}</span>
          <Button
            type="primary"
            ghost
            size="small"
            // onClick={() => navigate(`/vaadim-list?userId=${id}`)}
            onClick={() => {
              const userName = [userGetById.data?.fullName]
                .filter(Boolean)
                .join(" ");
              navigate(`/vaadim-list?userId=${id}`);
            }}
          >
            Vaads list
          </Button>
        </div>
      ),
    },
    {
      label: "Status",
      value: (
        <Badge
          color={getUserStatusColor(userGetById.data?.status || "deactivated")}
          text={
            <span className="capitalize-first-letter">
              {userGetById.data?.status}
            </span>
          }
        />
      ),
    },
    {
      label: "Join Date",
      value: userGetById.data?.createdAt
        ? dayjs(userGetById.data.createdAt).format("MMM DD, YYYY")
        : "-",
    },
  ];

  return (
    <SinglePageContainer
      title="Leader Info"
      onBack={() => navigate("/staff-management")}
    >
      <UserProfileLayout
        avatarCard={
          <AvatarCard
            name={userGetById.data?.fullName || ""}
            avatar={userGetById.data?.avatar}
            // subtitle={leader.subtitle}
            role={userGetById.data?.role}
          />
        }
      >
        <InfoSection
          title="Contact Information"
          items={contactItems}
          {...(isAdmin ? { onEdit: () => setEditOpen(true) } : {})}
        />

        <InfoSection title="General Information" items={generalItems} />
      </UserProfileLayout>
      <EditModal
        userId={id}
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
        }}
        setTableKey={() => userGetById.fetch()}
      />
    </SinglePageContainer>
  );
};

export default LeaderViewPage;
